import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, PageHeader, Badge } from '@/components/ui'
import { adminApi } from '@/lib/api'
import { Building2, Package, ShoppingCart, Users, Zap, HardDrive, Activity } from 'lucide-react'

export const Route = createFileRoute('/_app/')({
  component: PlatformDashboard,
})

interface DashboardData {
  tenants: { total: number; active: number }
  catalog: { totalProducts: number; totalCustomers: number }
  commerce: { ordersThisMonth: number; mrr: number }
  usage: { period: string; totalApiRequests: number; totalBandwidthGB: number; totalStorageGB: number }
  system: { healthy: boolean; checkedAt: string }
}

function PlatformDashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-dashboard-full'],
    queryFn: () => adminApi.get<{ data: DashboardData }>('/api/admin/dashboard'),
    refetchInterval: 30_000,
  })

  return (
    <>
      <PageHeader title="Dashboard de Plataforma" description="Vista global de todas las tiendas Core" />
      <div className="p-8 space-y-6">
        {error && (
          <Card className="p-4 bg-red-900/20 border-red-800">
            <p className="text-sm text-red-400">{(error as Error).message}</p>
          </Card>
        )}

        <section>
          <h2 className="text-sm font-medium text-neutral-400 mb-3 uppercase tracking-wider">Tiendas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={Building2} label="Tiendas totales" value={isLoading ? '—' : data?.data.tenants.total ?? 0}
              hint={data ? `${data.data.tenants.active} activas` : ''} color="text-blue-400" />
            <StatCard icon={Package} label="Productos en catálogo" value={isLoading ? '—' : data?.data.catalog.totalProducts ?? 0}
              hint="suma global" color="text-purple-400" />
            <StatCard icon={Users} label="Clientes finales" value={isLoading ? '—' : data?.data.catalog.totalCustomers ?? 0}
              hint="todos los tenants" color="text-green-400" />
          </div>
        </section>

        <section>
          <h2 className="text-sm font-medium text-neutral-400 mb-3 uppercase tracking-wider">Comercio (mes actual)</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={ShoppingCart} label="Pedidos este mes" value={isLoading ? '—' : data?.data.commerce.ordersThisMonth ?? 0}
              hint="órdenes confirmadas" color="text-yellow-400" />
            <StatCard icon={Activity} label="MRR" value={isLoading ? '—' : `$${data?.data.commerce.mrr ?? 0}`}
              hint="tras Stripe Billing" color="text-emerald-400" muted />
            <StatCard icon={Activity} label="Período" value={data?.data.usage.period ?? '—'} hint="actual" color="text-neutral-400" />
          </div>
        </section>

        <section>
          <h2 className="text-sm font-medium text-neutral-400 mb-3 uppercase tracking-wider">Uso agregado</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={Zap} label="API requests" value={isLoading ? '—' : (data?.data.usage.totalApiRequests ?? 0).toLocaleString()}
              hint="suma de tenants este mes" color="text-cyan-400" />
            <StatCard icon={HardDrive} label="Bandwidth" value={isLoading ? '—' : `${data?.data.usage.totalBandwidthGB ?? 0} GB`}
              hint="saliente del mes" color="text-orange-400" />
            <StatCard icon={HardDrive} label="Storage" value={isLoading ? '—' : `${data?.data.usage.totalStorageGB ?? 0} GB`}
              hint="R2 acumulado" color="text-pink-400" />
          </div>
        </section>

        <Card>
          <CardHeader><CardTitle>Salud del sistema</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <SystemRow label="Backend API (Workers)" status={data?.data.system.healthy ?? false} detail={data?.data.system.checkedAt ?? '—'} />
            <SystemRow label="Database (Neon)" status={data?.data.system.healthy ?? false} detail="HTTP driver" />
            <SystemRow label="Cache L2 (Upstash)" status={true} detail="REST API" />
            <SystemRow label="Storage (R2)" status={true} detail="Workers binding" />
            <SystemRow label="Email (Resend)" status={true} detail="sandbox mode" />
            <SystemRow label="Observability (PostHog)" status={true} detail="Cloud free tier" />
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function StatCard({ icon: Icon, label, value, hint, color, muted = false }: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number
  hint?: string
  color: string
  muted?: boolean
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-neutral-400 uppercase tracking-wider">{label}</p>
            <p className={`text-3xl font-bold mt-2 ${muted ? 'text-neutral-500' : ''}`}>{value}</p>
            {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
          </div>
          <Icon className={`size-7 ${color}`} />
        </div>
      </CardContent>
    </Card>
  )
}

function SystemRow({ label, status, detail }: { label: string; status: boolean; detail: string }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-neutral-800 last:border-0">
      <div>
        <p className="text-sm">{label}</p>
        <p className="text-xs text-neutral-500 mt-0.5">{detail}</p>
      </div>
      <Badge variant={status ? 'success' : 'destructive'}>{status ? 'OK' : 'DOWN'}</Badge>
    </div>
  )
}
